import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FLAG_LABEL, FLAGS_URL } from './pq-fix';

// =====================================================================
// The four steps for turning off Chrome's post-quantum key agreement, which costs
// roughly a second at the start of every call and which we cannot turn off for
// them — no WebRTC API exposes it, and a page cannot touch chrome://flags.
//
// Written for someone mid-shift who does not want to read anything. The tone is
// deliberately light: the honest answer to "is this safe?" is funnier than it is
// worrying, and a solemn security paragraph would make an SDR MORE nervous about
// a change that genuinely does not matter to them.
// =====================================================================

// Long enough to register as feedback, short enough that a second attempt does not
// feel blocked.
const COPIED_MS = 2000;

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="bg-muted text-muted-foreground flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-medium">
        {n}
      </span>
      <span className="pt-px">{children}</span>
    </li>
  );
}

function Faq({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <div className="font-medium">{q}</div>
      <p className="text-muted-foreground">{children}</p>
    </div>
  );
}

export function PqFixDialog({
  open,
  onClose,
  onApplied,
}: {
  open: boolean;
  onClose: () => void;
  onApplied: () => void;
}) {
  const ref = useRef<HTMLDialogElement | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  // Driven imperatively. Rendering the `open` ATTRIBUTE gives a non-modal dialog
  // with no top layer, no backdrop and no focus trap — losing the only reasons to
  // use <dialog> over a div in the first place.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  // The native close event, so Esc and the backdrop route through the same path as
  // the dismiss button — and none of them record the fix as applied.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener('close', onClose);
    return () => el.removeEventListener('close', onClose);
  }, [onClose]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = useCallback(() => {
    // Not available on an insecure origin, and can reject on a permission policy.
    // The URL is rendered in a selectable block regardless, so this is an
    // accelerator rather than the only way through.
    void navigator.clipboard
      ?.writeText(FLAGS_URL)
      .then(() => {
        setCopied(true);
        setCopyFailed(false);
      })
      .catch(() => setCopyFailed(true));
  }, []);

  return (
    <dialog
      ref={ref}
      className="bg-background text-foreground m-auto w-[min(32rem,calc(100vw-2rem))] rounded-lg border p-0 shadow-lg backdrop:bg-black/50"
      data-testid="pq-fix-dialog"
      aria-labelledby="pq-fix-title"
    >
      <div className="space-y-4 p-5 text-sm">
        <div className="flex items-center gap-2">
          <Zap className="size-5 text-amber-500" />
          <h2 id="pq-fix-title" className="text-base font-semibold">
            The magic go-faster button
          </h2>
        </div>

        {/* Above the steps, not buried in the FAQ: they are being asked to change a
            browser security setting and are owed the trade in plain words first. */}
        <p className="text-muted-foreground">
          <span className="text-foreground font-medium">What this does:</span> takes roughly{' '}
          <span className="text-foreground font-medium">a second</span> off the time between a lead
          picking up and you being able to hear them. Right now Chrome is doing extra cryptographic
          work that our calls don&rsquo;t need, and on long-distance connections it costs you the
          first moments of every conversation.
        </p>

        <ol className="space-y-3">
          <Step n={1}>
            <div className="space-y-1.5">
              <div>Copy this link — you can&rsquo;t click it, Chrome won&rsquo;t allow it.</div>
              <div className="flex items-center gap-2">
                <code className="bg-muted min-w-0 flex-1 truncate rounded px-2 py-1 font-mono text-[11px] select-all">
                  {FLAGS_URL}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 shrink-0 px-2 text-[11px]"
                  onClick={copy}
                  data-testid="pq-fix-copy"
                >
                  {copied ? <Check /> : <Copy />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              {copyFailed ? (
                <div className="text-muted-foreground text-[11px]">
                  Copying didn&rsquo;t work — select the link above and copy it manually.
                </div>
              ) : null}
            </div>
          </Step>
          <Step n={2}>Paste it into your address bar in another tab and press Enter.</Step>
          <Step n={3}>
            Set the highlighted <span className="font-medium">{FLAG_LABEL}</span> dropdown to{' '}
            <span className="font-medium">Disabled</span>.
          </Step>
          <Step n={4}>
            Click Chrome&rsquo;s <span className="font-medium">Relaunch</span> button at the bottom
            of that page.{' '}
            <span className="text-amber-600">Finish any call you&rsquo;re on first</span> —
            relaunching will end it.
          </Step>
        </ol>

        {/* Native disclosure: no state, no dependency, keyboard-accessible for free. */}
        <details className="border-t pt-3">
          <summary className="cursor-pointer text-[13px] font-medium">
            Wait, what am I actually turning off?
          </summary>
          <div className="mt-3 space-y-3 text-[13px]">
            <Faq q="What will this do to my computer?">
              By turning this off, if someone invents a working quantum computer they&rsquo;ll be
              able to listen in on your calls. Fortunately we don&rsquo;t expect that for five
              years, give or take fifty.
            </Faq>
            <Faq q="Seriously though?">
              Your calls stay encrypted exactly as they are today, with the same encryption banks
              and browsers have used for years. The setting you&rsquo;re turning off is insurance
              against a computer nobody has built yet. Everything else about your browser is
              untouched.
            </Faq>
            <Faq q="Chrome says these are experimental features. Is that bad?">
              That page is where Chrome puts settings it hasn&rsquo;t finished rolling out — in both
              directions. This one was switched on for everyone recently. You&rsquo;re putting it
              back.
            </Faq>
            <Faq q="Do I have to do this every day?">
              No. Once per computer. It survives restarts.
            </Faq>
            <Faq q="Can I undo it?">Yes — same link, set it back to Default.</Faq>
          </div>
        </details>

        <div className="flex items-center justify-end gap-2 border-t pt-3">
          {/* Dismissing must NOT record the fix — someone closing this to read it
              again later has not done anything, and marking them done would stop us
              prompting them at all. */}
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="pq-fix-dismiss">
            Not now
          </Button>
          <Button size="sm" onClick={onApplied} data-testid="pq-fix-applied">
            I&rsquo;ve done this
          </Button>
        </div>
        <p className="text-muted-foreground text-right text-[11px]">
          You should notice calls connecting faster straight away.
        </p>
      </div>
    </dialog>
  );
}
